<template>
    <div :class="['checkout', config.platform, mode]">
        <cart-nav
        :options="[ $t('cart_nav.choose_plan'), $t('cart_nav.confirm_details'), $t('cart_nav.complete') ]" :active="cartIndex"></cart-nav>
        <div class="checkout-container">
            <div :class="['checkout-container-inner', loading ? 'reo-processing' : '']" v-if="initialized">
                <div class="cart-main" v-if="id">
                    <h1 v-if="config.platform === 'web'">
                        {{ title }}
                    </h1>
                    <div v-if="mode === 'switch'" class="switch-title">
                        <h3 class="sub-title">{{ detailTitle }}</h3>
                        <p v-html="$t('cart.switch_note_1')"></p>
                    </div>
                    <h3 v-else class="sub-title">{{ detailTitle }}</h3>
                    <div class="cart-content">
                        <div>
                            <div>
                                {{ $t('cart.plan.title') }}
                            </div>
                            <div class="plan-title-col">
                                <span :style="{
                                    'background-color': color
                                }" class="plan-color"></span>
                                <span class="plan-title">{{ plan | planName }}</span>
                                <tip v-if="plan.metadata">
                                    <span v-html="$options.filters.planHistory(plan)"></span>{{ $t('comma') }}
                                    <span v-html="$options.filters.planCamera(plan)"></span>
                                    {{ !FEATURE('dashboard.used_space') ? $t('comma') : '' }}
                                    <span v-if="!FEATURE('dashboard.used_space')" v-html="$options.filters.planCapacity(plan)"></span>
                                </tip>
                            </div>
                        </div>
                        <div>
                            <div class="plan-price-copy">
                                {{ $t('cart.plan.price') }}
                            </div>
                            <div class="plan-price-wrap">
                                <del v-if="Number(this.plan.cycle.standard.regularPrice) !== Number(this.plan.cycle.standard.salePrice)">{{ regularPrice }}</del>
                                <span>{{ price }}</span>
                                <span class="plan-description" v-if="plan.description != ''">{{ plan.description }}</span>
                            </div>
                        </div>
                        <div>
                            <div>
                                {{ $t('cart.plan.valid') }}
                            </div>
                            <div>
                                <span class="red">{{ startedAt | timeFormat }} - {{ endingAt | timeFormat }}</span>
                            </div>
                        </div>
                        <div v-if="!free" class="plan-cycle-col">
                            <div>
                                <span>{{ $t('cart.plan.cycle') }}</span>
                                <tip>
                                    {{ $t( 'cart.cycle_tip' ) }}
                                </tip>
                            </div>
                            <div>
                                <div class="quantity">
                                    <input type="button" @click="quantity -= 1" class="minus" value="-">
                                    <input type="number" v-model.trim.number.lazy="quantity" :disabled="loading" :min="min" :max="max">
                                    <input type="button" @click="quantity += 1" class="plus" value="+">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="price-box">
                        <span class="total-title">{{ $t('cart.total') }}:</span>
                        <div class="total-price">
                            <del v-if="totalRegularPrice !== '' && !loading">{{currency}} {{totalRegularPrice}}</del>
                            <span>{{currency}} {{totalPrice}}</span>
                        </div>

                    </div>

                    <div class="switch-note" v-if="mode === 'switch'">
                        <h3 class="red">{{ $t('cart.switch.title') }}</h3>
                        <ul>
                            <li v-for="(item, index) in $t('cart.switch.list')" :key="index" v-html="item"></li>
                        </ul>
                        <p class="description" v-html="$t('cart.switch.help')"></p>
                        <div class="switch-agree-wrap">
                            <reo-checkbox v-model="switchAgreement" size="small" :text="$t('cart.switch.ok')"></reo-checkbox>
                        </div>
                    </div>
                    <hr v-if="!free"/>
                    <div class="payment" v-if="needPay">
                        <h3 class="sub-title">{{ $t('cart.payment.title') }}:</h3>
                        <template v-if="FEATURE('cloud.paypal')">
                            <div :class="['payment-content cn-payment', method.id === payment ? 'active' : '']" v-for="(method, index) in methods" :key="index">
                                <reo-radio v-model="payment" :label="method.id" type="circle" size="small"></reo-radio>
                                <div class="payment-desc">
                                    <img class="payment-main" :src="paymentMethods.cn[ method.gateway ]">
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <div :class="['payment-content', method.id === payment ? 'active' : '']" v-for="(method, index) in methods" :key="index">
                                <reo-radio v-model="payment" :label="method.id" type="circle" size="small"></reo-radio>
                                <div class="payment-desc">
                                    <span class="main-payment">{{ $t('cart.payment.pay_via') }} <img class="payment-main" :src="paymentMethods[ method.title ].main"></span>
                                    <span class="bracket">(</span>
                                        <img v-for="(img, index) in paymentMethods[ method.title ].support" :key="index" :src="img" class="payment-ways" :title="$t(`cart.credict_cart.${index}`)"/>
                                    <span class="bracket">)</span>
                                </div>
                                <tip>
                                    {{ $t( 'cart.payment_tip' ) }}
                                    <a href="https://www.paypal.com/us/webapps/mpp/paypal-popup" class="ui-link" target="_blank">
                                        <span class="text">{{$t( 'cart.payment_tip_link' )}}</span>
                                        <i class="ui-icon icon-icon_arrow-right"></i>
                                    </a>
                                </tip>
                            </div>
                        </template>
                    </div>
                    <div :class="['agreement', mode]" v-if="!free">
                        <reo-checkbox v-model="agreement" size="small" class="checkbox-left"></reo-checkbox>
                        <p v-html="$t('cart.agreement', {
                            conditions: getOfficialUrl( config.conditions ),
                            privacy: getOfficialUrl( config.privacy )
                        })"></p>
                    </div>
                    <p :class="['cart-button', mode]">
                        <button
                        :class="['checkout-button', 'ui-button', (mode === 'subscribe') ? 'red-button' : 'primary-button']"
                        :disabled="subscripting"
                        :ga-label="`Cart confirm(${mode})`"
                        @click="checkoutConfirm">{{ checkoutButton }}</button>
                        <router-link
                        :class="['ui-button', 'empty-button']"
                        :ga-label="`Cart cancel(${mode})`"
                        to="/user/dashboard/"
                        v-if="!(mode === 'subscribe' || (needPay && mode === 'renew') || config.platform === 'app')">{{ cancelButton }}</router-link>
                    </p>
                    <cart-total
                    v-if="mode === 'subscribe'"
                    @subscribe="_subscriptions"
                    :loading="loading"
                    :button="checkoutButton"
                    :disabled="subscripting"></cart-total>
                    <p class="switch-statement red" v-if="mode === 'switch'" v-html="$t('cart.switch_note_2')"></p>
                </div>
                <!-- empty cart -->
                <div class="empty-cart" v-else>
                    <i class="icon-icon_cart-2-101"></i>
                    <h4>{{ $t( 'cart.empty.title' ) }}</h4>
                    <router-link class="ui-button primary-button" to="/">{{ $t( 'cart.empty.button' ) }}</router-link>
                </div>
                <cart-bottom v-if="config.platform === 'web'"></cart-bottom>
            </div>
        </div>
        <reo-dialog
        v-if="showCountryLimitNotice"
        :labelOfConfirm="$t('cart.countryLimitConfirm')"
        :labelOfCancel="$t('cancel')"
        :title="$t('cart.limitTitle')"
        :supportTip="$t( 'cart.supportTip' )"
        :supportList="[
            {
                title: $t( 'cart.limitCountries' ),
                content: countrySupported
            },
            {
                title: $t( 'cart.limitProducts' ),
                content: productSupported
            }
        ]"
        @confirm="_subscriptions"
        @cancel="showCountryLimitNotice = false"
        ></reo-dialog>
        <reo-dialog
        v-if="showPaypalDialog"
        class="paypal-dialog"
        title="paypal"
        labelOfConfirm="confirm"
        labelOfCancel="cancel"
        @confirm="_subscriptions"
        @cancel="showPaypalDialog = false; payerAccount = '';">
            <div class="paypal-dialog-inner">
                <label for='payerAccount'>payerAccount:<span class='warn-tips'>*</span></label>
                <div>
                    <input id='payerAccount' type="text" v-model.trim="payerAccount" placeholder="please enter your payerAccount" autocomplete="false">
                    <p class='err-tips' v-show='!payerAccount.length && showTips'>{{$t('errors.payer_account_required')}}</p>
                </div>
            </div>
        </reo-dialog>
    </div>
</template>

<script>
import reoCheckbox from '@/basic/Checkbox';
import reoRadio from '@/basic/Radio';
import { getPlan, previewSubscriptions, subscriptions, getPaymentsMethods } from '@/api/cart';
import cartTotal from '@/components/cartTotal';
import cartNav from '@/components/cartNav';
import { orderedProductInfo } from '@/api/plan';
import paymentMethods from '@/config/paymentMethods';
import cartBottom from '@/components/cartBottom';
import { unfoldError } from '@/common/js/notice';
import {subscription as paymentStatus, plan as planStatus} from '@/config/status';
import Tip from '@/basic/Tip';
import { config } from 'CONFIG';
import ReoDialog from '@/basic/Dialog';
import { getCloudConfig, listToString } from '@/api/s3Config';
import store from '@/store';
import reolinkGa from '@/common/js/gtagPageview';
import { requestToApp } from '@/api/app';

export default {

    components: {

        reoCheckbox,
        reoRadio,
        cartTotal,
        cartNav,
        cartBottom,
        Tip,
        ReoDialog

    },
    async created () {

        if ( !this.id ) {

            this.loading = false;
            return false;

        }
        await this.getPayments();
        let prevSub = await orderedProductInfo();
        if ( prevSub && ( prevSub.status === paymentStatus.ACTIVATING || prevSub.status === paymentStatus.DEACTIVATING )) {

            unfoldError( 'errors.sub_processing', 9999999 );
            return false;

        }
        this.prevSub = prevSub;

        if ( prevSub && this.id !== prevSub.plan.id ) {

            this.mode = 'switch';
            if ( config.platform !== 'web' ) {

                requestToApp( 'modifyPageTitle', {title: this.$t( 'cart.appTitle.switch' )});

            }

        } else if ( prevSub && this.id === prevSub.plan.id ) {

            this.mode = 'renew';
            if ( config.platform !== 'web' ) {

                requestToApp( 'modifyPageTitle', {title: this.$t( 'cart.appTitle.renew' )});

            }

        } else {

            this.mode = 'subscribe';
            if ( config.platform !== 'web' ) {

                requestToApp( 'modifyPageTitle', {title: this.$t( 'cart.appTitle.subscribe' )});

            }

        }

        // 更新当前的统计状态
        reolinkGa.subscribeMode = this.mode;
        reolinkGa.prevPlan = prevSub ? prevSub.plan.id : 0;
        reolinkGa.nextPlan = this.id;
        reolinkGa.sendSubscribeMode();

        let plan;
        try {

            plan = await getPlan( this.id );

        } catch ( err ) {

            unfoldError( 'errors.plan_not_available', 9999999 );
            return false;

        }
        this.plan = plan;
        if ( plan.status === planStatus.DISABLED ) {

            unfoldError( 'errors.plan_not_available', 9999999 );
            return false;

        }
        this.currency = this.$t( `CURRENCY.${plan.currency}` );
        let free = Number( plan.cycle.standard.salePrice ) === 0;
        this.free = free;

        // 预加载cloud的国家支持列表，创建订单之前用到
        this.countryRes = await this.getCountrySupported();

        /**
         * 如果购买目标套餐是免费的，则最多选一个
         * 如果购买套餐是活动套餐，则最多加购六个
         */

        if ( free ) {

            this.max = 1;
            await this.preview( 1 );

        } else if ( plan.id === this.cloudConfig.activityPlanID ) {

            this.max = 6;
            await this.preview( 1 );

        } else if ( prevSub && this.id !== prevSub.plan.id && prevSub.plan.cycle.standard.salePrice !== 0 ) {

            /**
             * 收费套餐切换至收费套餐
             */
            await this.preview( 0 );

        } else {

            await this.preview( 1 );

        }
        
        this.initialized = true;

        this.descriptionList = [
            {
                title: '',
                content: ''
            }
        ];

    },
    data () {

        return {

            plan: {},
            id: Number( this.$route.query.plan_id ),
            quantity: 1,
            lastQuantity: 1,
            min: 1,
            max: 2048,
            totalPrice: 0,
            currency: '',
            /**
             * 用户选择的支付方式
             */
            payment: 0,
            /**
             * 支付时需要同意的条款
             */
            agreement: false,
            /**
             * 支持的支付方式
             */
            methods: [],
            loading: true,
            cartIndex: 1,
            /**
             * 支付方式对应的图标
             */
            paymentMethods,
            /**
             * 预览的套餐起始时间
             */
            startedAt: 0,
            endingAt: 0,
            /**
             * 目标套餐为免费套餐
             */
            free: true,
            /**
             * 切换时需要同意的条款
             */
            switchAgreement: false,
            /**
             * 当前是否需要付钱
             */
            needPay: false,
            /**
             * 正在订阅
             */
            subscripting: false,
            mode: '',
            config,
            initialized: false,
            prevSub: {},
            showCountryLimitNotice: false,
            showPaypalDialog: false,
            countryRes: null,
            cloudConfig: {},
            payerAccount: '',
            showTips: false
        };

    },
    methods: {

        async getPayments () {

            try {

                let methods = await getPaymentsMethods();
                this.methods = methods;
                this.payment = methods[ 0 ].id;

            } catch ( err ) {

                console.error( err );

            }

        },
        async checkoutConfirm () {

            if ( !this.FEATURE( 'cloud.country_select' )) {

                let countries = await this.countryRes;
                let userCountry = store.state.user.user.country;
                if ( !countries.some( item => item.code === userCountry )) {
    
                    this.showCountryLimitNotice = true;
    
                } else {
    
                    this._subscriptions();
    
                }

            } else {

                this._subscriptions();
                
            }

        },
        getCountrySupported () {

            let _this = this;
            return new Promise( function ( resolve, reject ) {

                getCloudConfig().then( config => {

                    _this.cloudConfig = config;
                    resolve( config.countriesSupported );

                }).catch( function ( err ) {

                    reject( err );

                });

            });

        },
        async preview ( quantity ) {

            this.loading = true;
            try {

                let data = await previewSubscriptions( this.id, quantity );
                let { payment, subscription } = data;
                console.log( 'pay', JSON.stringify( payment ));
                let totalPrice = 0;

                /**
                 * 进入页面首次预览订单尝试购买0个（余额足够七天）
                 *
                 * 如果返回不需要付钱，则可以直接切换或者购买
                 *
                 * 如果返回需要付钱，则至少需要购买一周期
                 */

                if ( !payment.amount ) {

                    this.quantity = quantity;
                    this.lastQuantity = quantity;
                    if ( quantity === 0 ) {

                        this.min = 0;

                    }
                    this.needPay = false;

                } else {

                    this.needPay = true;
                    if ( quantity === 0 ) {

                        this.quantity = 1;
                        this.lastQuantity = 1;
                        this.min = 1;

                    } else {

                        this.lastQuantity = quantity;
                        this.quantity = quantity;

                    }
                    totalPrice = payment.amount.value;

                }
                this.startedAt = subscription.cycle.startedAt;
                this.endingAt = subscription.cycle.endingAt;
                this.totalPrice = totalPrice;
                this.loading = false;

            } catch ( err ) {

                let code = err.response.data.error.code;
                this.errorHandle( code, 'errors.error_unknow' );
                console.error( err );

            }

        },
        async _subscriptions () {

            this.showCountryLimitNotice = false;
            this.subscripting = true;
            
            // 如果是切换套餐，须同意切换条款
            if ( this.mode === 'switch' && !this.switchAgreement ) {

                unfoldError( 'errors.checkbox_required' );
                window.scrollTo( 0, $( '.switch-note' ).offset().top );
                this.subscripting = false;

            } else if ( this.needPay && !this.agreement ) {

                // 如果需要支付，须同意支付条款
                unfoldError( 'errors.checkbox_required' );
                window.scrollTo( 0, $( '.agreement' ).offset().top - 200 );
                this.subscripting = false;

            } else {

                if ( this.showPaypalDialog && !this.payerAccount.trim().length ) {

                    this.showTips = true;
                    return;

                }

                try {

                    await subscriptions( this.id, this.quantity, this.payment, this.prevSub, this.needPay, this.mode, this.payerAccount );

                } catch ( err ) {

                    let code = err.response.data.error.code;
                    this.showPaypalDialog = false;
                    
                    if ( code === 53283 ) {

                        this.showPaypalDialog = true;

                    } else {

                        this.errorHandle( code, 'errors.server_error' );

                    }
                    this.showTips = false;
                    this.subscripting = false;

                }

            }

        },
        errorHandle ( code, defaultError ) {

            let key = defaultError;
            if ( code === 53263 ) {

                /**
                 * 免费套餐现在不可续订
                 */
                key = 'errors.can_not_renew';

            } else if ( code === 53259 ) {

                /**
                 * 频繁切换
                 */
                key = 'errors.can_not_switch';

            } else if ( code === 53274 ) {

                /**
                 * disabled plan
                 */
                key = 'errors.plan_not_available';

            } else if ( code === 53282 ) {

                /**
                 * 购买或切换仅供续费的套餐
                 */
                key = 'errors.plan_renew_only';

            } else if ( code === 53279 ) {

                /**
                 * 需要退款，但是已有一个正在处理的退款
                 */
                key = 'errors.cannot_update_refund';

            }
            unfoldError( key, 9999999 );

        }

    },
    computed: {
        totalRegularPrice () {

            let price = Number( this.plan.cycle.standard.regularPrice ) * this.quantity;
            let totalRegularPrice = price.toFixed( 2 );

            if ( Number( this.totalPrice ) === Number( totalRegularPrice )) {

                return '';

            }

            return price.toFixed( 2 );

        },

        price () {

            let plan = this.plan;
            if ( plan.cycle ) {

                let cycle = plan.cycle.standard;
                return `${this.$t( `CURRENCY.${plan.currency}` )} ${cycle.salePrice}/${this.$tc( `cycle_unit.${cycle.interval.unit}`, cycle.interval.frequency, {
                    count: cycle.interval.frequency
                })}`;

            }
            return '';

        },
        regularPrice () {

            let plan = this.plan;
            if ( plan.cycle ) {

                let cycle = plan.cycle.standard;
                return `${this.$t( `CURRENCY.${plan.currency}` )} ${cycle.regularPrice}/${this.$tc( `cycle_unit.${cycle.interval.unit}`, cycle.interval.frequency, {
                    count: cycle.interval.frequency
                })}`;

            }
            return '';

        },
        title () {

            let { mode, prevSub, plan } = this;
            let title = '';
            if ( !mode ) {

                return '';

            }
            if ( mode === 'switch' ) {

                title = prevSub.plan.title;

            } else {

                title = plan.title;

            }
            return mode ? this.$t( `cart.title.${mode}`, { plan: title }) : '';

        },
        detailTitle () {

            let { mode } = this;
            return mode ? this.$t( `cart.plan_details_title.${mode}` ) : '';

        },
        color () {

            let plan = this.plan;
            return plan.metadata ? plan.metadata[ 'plan.display_color' ] : 'none';

        },
        checkoutButton () {

            let { mode, needPay } = this;
            let pay = !needPay ? 'free' : 'pay';
            return mode ? this.$t( `cart.checkout.${mode}.${pay}` ) : '';

        },
        cancelButton () {

            let { mode } = this;
            return mode ? this.$t( `cart.cancel.${mode}` ) : '';

        },
        countrySupported () {

            let countrySupported = this.cloudConfig.countriesSupported;
            if ( !countrySupported || !countrySupported.length ) return '';
            let locale = store.state.lang.lang;
            let texts = countrySupported.map( item => item[ locale ] );
            if ( !texts || !texts.length ) {

                return '';

            }
            return listToString( texts );

        },
        productSupported () {

            let productSupported = this.cloudConfig.productSupported;
            if ( !productSupported || !productSupported.length ) return '';
            let texts = productSupported.map( item => item.product );
            if ( !texts || !texts.length ) {

                return '';

            }
            return listToString( texts );

        }

    },
    watch: {

        quantity ( newQuantity, oldQuantity ) {

            if ( !this.initialized ) {

                return false;

            }
            if ( newQuantity % 1 !== 0 ) {

                this.quantity = Math.round( newQuantity );

            } else if ( newQuantity > this.max ) {

                this.quantity = this.max;

            } else if ( newQuantity < this.min || newQuantity === '' ) {

                this.quantity = this.min;

            } else if ( newQuantity !== this.lastQuantity ) {

                this.preview( newQuantity );

            }

        }

    }
};
</script>

<style lang="less">
    body .payment-content .reo-tip{
        position: absolute;
        right: 0;
        top: -8px;
        @media @mobile{
            &>.content{
                left: inherit;
                right: 0;
            }
        }
        &.subscribe.app{
            padding-bottom: 1rem;
        }
    }
    .switch-agree-wrap{
        .checkbox-label{
            color: #333;
        }
        .checkbox-wrap-container .text{
            font-weight: 500;
        }
    }
    body .plan-title-col .reo-tip,
    body .plan-cycle-col .reo-tip{
        margin-left: 0.15rem;
    }
    @media @mobile{
        body .plan-title-col .reo-tip .content{
            left: inherit;
            right: 0;
        }
    }
</style>

<style scoped lang="less">
    @border-color: #DBDBDB;
    @quantity-border: #c6c6c6;

    .paypal-dialog .paypal-dialog-inner {
        width: 6rem;
        height: 1rem;
        font-size: 0.24rem;
        display: flex;

        label{
            height: 0.46rem;
            line-height: 0.46rem;
            margin-right: 0.2rem;

            .warn-tips{
                color: #f00;
                margin-left: 0.05rem;
            }
        }

        div{
            width: 100%;

            input{
                outline: none;
                -webkit-box-shadow: none;
                box-shadow: none;
                padding: 0;
                border: 1px solid #ccc;
                height: 0.46rem;
                -webkit-box-sizing: border-box;
                box-sizing: border-box;
                width: 100%;
                font-size: 0.18rem;
                border-radius: 0.05rem;
                padding: 0 0.16rem;
            }

            .err-tips{
                color: #f00;
                font-size: 0.18rem;
                margin-top: 0.1rem;
                text-align: left;
            }
        }
    }
    .checkout{
        background-color: #F3F4F9;
        padding: 0.6rem 0;
        @media @mobile{
            padding: 0;
            background-color: #fff;
        }
    }
    .checkout.app .checkout-container{
        padding-bottom: 0.5rem;
    }
    .checkout-container{
        max-width: 1200px;
        margin: 0 auto;
        background-color: #fff;
        padding: 0.6rem 0;
        border-radius: 0.24rem;
        @media @mobile{
            padding: 1rem 0 0;
        }
    }
    .checkout-container-inner{
        max-width: 760px;
        margin: auto;
    }
    .cart-main{
        padding-bottom: 1rem;
        position: relative;
        @media @mobile{
            padding: 0 15px 0.8rem;
        }
    }
    h1{
        margin-bottom: 0.4rem;
        font-size: 0.36rem;
        font-weight: 700;
        text-align: center;
        @media @mobile{
            font-size: 0.64rem;
            font-weight: 500;
            margin-bottom: 0.6rem;
        }
    }
    .payment .sub-title{
        margin-bottom: 0.2rem;
        @media @mobile{
            margin-bottom: 0.4rem;
        }
    }
    .sub-title{
        font-size: 0.2rem;
        font-weight: 700;
        margin-bottom: 0.05rem;
        @media @mobile{
            margin-bottom: 0.3rem;
            font-size: 0.36rem;
        }
    }
    .switch-title{
        margin-bottom: 0.4rem;
        .sub-title{
            margin-bottom: 0.05rem;
        }
        p{
            font-size: 0.16rem;
            @media @mobile{
                font-size: 0.26rem;
            }
        }
    }
    .cart-content{
        padding: 0.4rem 0.6rem;
        border-radius: 0.09rem;
        background-color: #f7f8f9;
        font-size: 0.16rem;
        margin-bottom: 0.3rem;
        border: 1px solid #eee;
        .plan-price-copy {
            vertical-align: top;
        }
        .plan-price-wrap {
            vertical-align: top;
            .plan-description {
                display: block;
                color: #E74330;
                font-size: .16rem;
                margin-top: .07rem;
                line-height: 1.2;
                @media @mobile {
                    font-size: .24rem;
                }
            }
            del {
                font-weight: 400;
                color: #777;
                margin-right: 16px;
                & + span {
                    font-weight: 500;
                }
                @media @mobile{
                    display: block;
                    margin: 0;
                    margin-bottom: 0.06rem;
                }
            }
            @media @mobile {
                width: calc(100% - 2.2rem);
            }
        }

        @media @mobile{
            padding: 0.6rem 0.3rem;
            font-size: 0.26rem;
            border-radius: 0.18rem;
            margin-bottom: 0.5rem;
        }
        &>div+div{
            margin-top: 0.2rem;
        }
        &>div>div{
            display: inline-block;
            &:first-child{
                width: 2.1rem;
                font-weight: 500;
            }
        }
        .plan-title{
            font-weight: 500;
            font-size: 0.2rem;
            vertical-align: middle;
            @media @mobile{
                font-size: 0.26rem;
            }
        }
        .plan-color{
            width: 0.18rem;
            height: 0.18rem;
            border-radius: 100%;
            display: inline-block;
            vertical-align: middle;
            margin-right: 0.05rem;
            @media @mobile{
                width: 0.24rem;
                height: 0.24rem;
            }
        }
    }
    .quantity{
        font-size: 0;
        display: inline-block;
        vertical-align: middle;
        input{
            text-shadow: none;
            background-color: #f7f8f9;
            border: 1px solid @quantity-border;
            color: #777;
            font-weight: 400;
            text-align: center;
            height: 0.25rem;
            box-sizing: border-box;
            font-size: 0.16rem;
            vertical-align: middle;
            font-weight: 500;
        }
        input[type='button']{
            padding: 0 10px;
            text-transform: none;
            display: inline-block;
            cursor: pointer;
            &:hover{
                background-color: rgba(0,0,0,0.2);
            }
        }
        input[type='number']{
            border-left: 0;
            border-right: 0;
            box-shadow: none;
            width: 0.28rem;
            font-weight: 500;
            -webkit-appearance: textfield;
            margin: 0;
            @media @mobile{
                width: 0.6rem;
                border-radius: 0;
            }
        }
        .minus{
            border-right: 0;
        }
        .plus{
            border-left: 0;
        }
    }
    .plan-cycle-col>div>span{
        vertical-align: middle;
    }
    @media screen and (max-width: 1199px) {
        .quantity{
            input{
                height: 0.52rem;
                font-size: 0.3rem;
                background-color: #eaeaea;
            }
            input[type='button'] {
                box-sizing: border-box;
                width: 0.5rem;
                border: none;
                &.minus{
                    border-radius: 100% 0 0 100%;
                }
                &.plus{
                    border-radius: 0 100% 100% 0;
                }
            }
            input[type='number']{
                border: none;
                color: #333;
            }
        }
    }
    .price-box{
        text-align: right;
        margin-bottom: 0.6rem;
        font-size: 0.22rem;
        .total-price{
            display: inline-block;
            font-weight: 500;
            font-size: 0.26rem;
            del {
                font-size: 0.2rem;
                color: #777;
                font-weight: 400;
                margin: 0 12px;
            }
        }
        .total-title{
            float: left;
        }
        @media @mobile{
            &:after{
                content: '';
                display: table;
                clear: both;
            }
            .total-price{
                font-size: 0.36rem;
                del {
                    font-size: 0.3rem;
                    margin: 0 0.12rem;
                }
            }
            .total-title{
                font-size: 0.3rem;
            }
        }
    }
    hr{
        border: none;
        border-bottom: 1px solid #ddd;
        margin-bottom: 0.4rem;
        @media @mobile{
            display: none;
        }
    }
    .support{
        max-width: 875px;
        margin-bottom: 0.2rem;
    }
    .payment{
        .payment-content{
            border-radius: 8px;
            border: 1px solid @border-color;
            padding: 0.4rem 0 0.4rem 0.3rem;
            font-size: 0;
            font-weight: 500;
            margin-bottom: 0.2rem;
            position: relative;
            box-shadow: 0 0 6px 1px rgba(0, 0, 0, 0.12);
            @media @mobile{
                margin-bottom: 0.4rem;
                padding-top: 0.44rem;
                padding-bottom: 0.44rem;
                .bracket{
                    display: none;
                }
            }
            .credit-cart-text{
                display: none;
                @media @mobile{
                    display: inline;
                }
            }
            @media @mobile{
                .credit-cart{
                    display: block;
                }
            }
            .payment-desc{
                display: inline-block;
                margin-left: 0.2rem;
                vertical-align: middle;
                font-size: 0.16rem;
                @media @mobile{
                    font-size: 0.27rem;
                }
            }
            &.active{
                border: 1px solid @theme;
            }
            .payment-main{
                width: 0.6rem;
                vertical-align: middle;
                @media @mobile{
                    width: 1.2rem;
                }
            }
            &.cn-payment {
                .payment-main {
                    width: auto;
                    max-width: unset;
                    height: 30px;
                    @media @mobile{
                        height: .4rem;
                    }
                }
            }
            .payment-ways{
                width: 0.3rem;
                vertical-align: middle;
                @media @mobile{
                    width: 0.65rem;
                    max-width: 66px;
                }
            }
            .payment-ways+.payment-ways{
                margin-left: 0.1rem;
            }
        }
    }
    .agreement{
        padding-left: 0.3rem;
        margin-bottom: 0.8rem;
        position: relative;
        .checkbox-left{
            position: absolute;
            left: 0;
            top: 0;
        }
        p{
            font-size: 0.16rem;
        }
        @media @mobile{
            padding-left: calc(22px + .2rem);
            font-size: 0.24rem;
            margin-bottom: 0.6rem;
            &.subscribe{
                margin-bottom: 0;
            }
            p{
                font-size: 0.26rem;
                line-height: 0.36rem;
            }
        }
    }
    .cart-button{
        text-align: center;
        .ui-button+.ui-button{
            margin-left: 0.4rem;
        }
        @media @mobile{

            .ui-button{
                width: 100%;
                box-sizing: border-box;
            }
            .ui-button+.ui-button{
                margin: 0.2rem 0 0;
            }
            &.subscribe .checkout-button{
                display: none;
            }
            &.subscribe{
                display: none;
            }
        }
    }
    .switch-note{
        background-color: #FAFBFC;
        padding: 0.4rem 0.6rem;
        border-radius: 0.08rem;
        border: 1px solid #eee;
        margin-bottom: 0.6rem;
        @media @mobile{
            padding: 0.6rem 0.3rem;
            border-radius: 0.18rem;
        }
        h3{
            font-weight: 700;
            font-size: 0.2rem;
            padding-bottom: 0.2rem;
            margin-bottom: 0.2rem;
            border-bottom: 1px solid #ccc;
            @media @mobile{
                font-size: 0.36rem;
                padding-bottom: 0.3rem;
                margin-bottom: 0.3rem;
            }
        }
        h4{
            font-size: 0.2rem;
            font-weight: 700;
            margin-bottom: 0.15rem;
            @media @mobile{
                font-size: 0.36rem;
                margin-bottom: 0.3rem;
            }
        }
        ul{
            padding-left: 0.2rem;
            margin-bottom: 0.15rem;
            li{
                font-size: 0.16rem;
                list-style: disc;
            }
            @media @mobile{
                padding-left: 0.4rem;
                margin-bottom: 0.3rem;
                li{
                    font-size: 0.26rem;
                }
                li+li{
                    margin-top: 0.15rem;
                }
            }
        }
        .more-details{
            margin-bottom: 0.25rem;
            display: inline-block;
            @media @mobile{
                margin-bottom: 0.44rem;
            }
        }
        .description{
            border-top: 1px solid #ccc;
            padding-top: 0.2rem;
            margin-bottom: 0.15rem;
            font-size: 0.16rem;
            @media @mobile{
                padding-top: 0.44rem;
                font-size: 0.24rem;
                margin-bottom: 0.22rem;
            }
        }
        .switch-agree{
            font-size: 0.18rem;
            font-weight: 500;
            vertical-align: middle;
            display: inline-block;
            margin-left: 0.1rem;
            @media @mobile{
                font-size: 0.26rem;
                margin-left: 0.15rem;
            }
        }
        @media @mobile{
            .switch-agree-wrap{
                margin-bottom: 0.6rem;
            }
        }
    }
    .switch-statement{
        position: absolute;
        bottom: 0.5rem;
        transform: translateY(50%);
        text-align: center;
        font-size: 0.16rem;
        @media @mobile{
            margin-top: 0.6rem;
            position: static;
            transform: none;
            text-align: left;
            font-size: 0.26rem;
        }
    }
    .save-advance{
        font-size: 0.16rem;
        @media @mobile{
            font-size: 0.26rem;
        }
    }
    .empty-cart{
        text-align: center;
        padding-bottom: 1rem;
        @media @mobile{
            padding: 1rem 15px 2rem;
            .ui-button{
                box-sizing: border-box;
                width: 100%;
            }
        }
        h4{
            font-size: 0.2rem;
            font-weight: 700;
            margin-bottom: 0.5rem;
            @media @mobile{
                font-size: 0.36rem;
                margin-bottom: 1.2rem;
            }
        }
        i{
            font-size: 1.5rem;
            color: #e0e0e0;
            margin-bottom: 0.3rem;
            display: inline-block;
            @media @mobile{
                font-size: 2.7rem;
                margin-bottom: 0.6rem;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// src/view/cart/cart.vue